import { initializeApp } from 'firebase/app';
import { initializeAuth, indexedDBLocalPersistence, browserPopupRedirectResolver } from 'firebase/auth';
import { getStorage } from 'firebase/storage';
import { getFunctions } from 'firebase/functions';
import { initializeFirestore, setLogLevel, doc, getDocFromServer } from 'firebase/firestore';
import firebaseConfig from '../firebase-applet-config.json';

console.log("Firebase Config loaded:", {
  projectId: firebaseConfig.projectId,
  databaseId: firebaseConfig.firestoreDatabaseId
});

export const app = initializeApp(firebaseConfig);
export { firebaseConfig };

// Use initializeAuth for better control and stability in iframe environments
export const auth = initializeAuth(app, {
  persistence: indexedDBLocalPersistence,
  popupRedirectResolver: browserPopupRedirectResolver
});

// Set log level to error to avoid transport warning noise in the console
setLogLevel('error');

const databaseId = firebaseConfig.firestoreDatabaseId || "(default)";

// Use initializeFirestore to enable specific settings like long polling
// this is often needed in iframe/proxied environments
export const db = initializeFirestore(app, {
  experimentalForceLongPolling: true
}, databaseId);

export const storage = getStorage(app);
export const functions = getFunctions(app, 'europe-west1');

// Test Firestore connection
async function testConnection(retries = 3) {
  try {
    console.log(`[Firebase] Testing connection to database: "${databaseId}" (Attempt ${4 - retries}/3)`);
    const docRef = doc(db, 'test', 'connection');
    // Use getDocFromServer to bypass cache and check real connectivity/permissions
    await getDocFromServer(docRef);
    console.log("[Firebase] Firestore connection successful.");
  } catch (error: any) {
    console.error("[Firebase] Firestore connection test failed:", {
      message: error.message,
      code: error.code,
      databaseId: databaseId,
      projectId: firebaseConfig.projectId,
      stack: error.stack
    });
    
    if (retries > 0 && error.message?.includes('the client is offline')) {
      console.log(`[Firebase] Retrying connection in 2s...`);
      setTimeout(() => testConnection(retries - 1), 2000);
      return;
    }

    if (error.message?.includes('PERMISSION_DENIED') || error.code === 'permission-denied') {
      console.warn("[Firebase] Permission denied. This usually means the security rules are not allowing access to /test/connection or the database is not yet ready.");
    }
    
    if ((error.message?.includes('NOT_FOUND') || error.code === 'not-found') && (databaseId as string) !== '(default)') {
      console.warn("[Firebase] Named database not found. This might be because it's still being provisioned or the ID is incorrect.");
    }
    
    if(error.message?.includes('the client is offline')) {
      console.error("[Firebase] The client is offline error PERSISTS. This often indicates a network proxy issue, invalid API key restrictions, or Firestore API being disabled in the project.");
    }
  }
}

// Defer connection test to avoid blocking app boot
setTimeout(() => {
  testConnection();
}, 5000);
